import dayjs from 'dayjs'
import { createSignal, createEffect, createMemo, onMount, onCleanup, For } from 'solid-js'
import { reconcile } from 'solid-js/store'
import { Portal } from 'solid-js/web'
import { addToast } from '@/components/Toast'
import { CopyLine } from '@/icons'
import * as constants from '@lib/constants'
import * as games from '@games'
import { events, GAME_INFO, GAME_STARTED, BET, BET_DONE } from '@/store/messenger'
import { res2json, expiresDays, hourPadStart } from '@/utils'
import account, { access, updateAccount } from '@/store/account'
import { store, setStore } from '@/store'

const [expiresAt, setExpiresAt] = createSignal('')
const [gameSwitch, setGameSwitch] = createSignal(false)
const [registerSwitch, setRegisterSwitch] = createSignal(false)
const [repeatingSwitch, setRepeatingSwitch] = createSignal(false)

const [bets, setBets] = createSignal([])

export default function Dashboard() {
  const [loading, setLoading] = createSignal(true)
  const [gameInfo, setGameInfo] = createSignal({})
  const validBetAmount = createMemo(() => bets().filter(e => !e.is_robot && e.status === 'betted').map(e => e.amount).reduce((l, r) => l + r, 0))

  onMount(() => {
    const jobs = [
      fetch(`/api/${account().game_code}/expires_at`).then(res2json).then(r => setExpiresAt(r.data?.value)),
    ]
    if (access('game.account.edit')) {
      jobs.push(
        fetch(`/api/${account().game_code}/settings/repeating`).then(res2json).then(r => setRepeatingSwitch(r.data?.value === 'on')),
        fetch(`/api/${account().game_code}/settings/game`).then(res2json).then(r => setGameSwitch(r.data?.value === 'on')),
      )
    }
    if (access('game.account.view')) {
      jobs.push(
        fetch(`/api/${account().game_code}/betting/current`).then(res2json).then(r => { setBets(r.data ?? []) }),
      )
    }
    if (access('settings.edit')) {
      jobs.push(
        fetch(`/api/${account().game_code}/settings/register`).then(res2json).then(r => setRegisterSwitch(r.data?.value === 'on')),
      )
    }
    Promise.allSettled(jobs).finally(() => { setLoading(false) })
  })

  let lastGameNum = ''
  function onGameInfo(data) {
    if (data.game === account().game) {
      setGameInfo(data ?? {})
      if (lastGameNum !== data?.num) {
        lastGameNum = data?.num
      }
    }
  }
  function onGameStarted() {
    setBets([])
  }
  let fetchingBetStatus = false
  function onBetStatusUpdated(data) {
    if (fetchingBetStatus) {
      return
    }
    fetchingBetStatus = true
    setTimeout(() => {
      fetch(`/api/${account().game_code}/betting/current`).then(res2json).then(r => { setBets(r.data ?? []) }).finally(() => { fetchingBetStatus = false })
    }, 50)
  }
  onMount(() => {
    events.on(GAME_INFO, onGameInfo)
    events.on(GAME_STARTED, onGameStarted)
    events.on(BET, onBetStatusUpdated)
    events.on(BET_DONE, onBetStatusUpdated)
  })
  onCleanup(() => {
    events.removeListener(BET_DONE, onBetStatusUpdated)
    events.removeListener(BET, onBetStatusUpdated)
    events.removeListener(GAME_STARTED, onGameStarted)
    events.removeListener(GAME_INFO, onGameInfo)
  })

  const fetchGameInfo = () => fetch(`/api/${account().game_code}/current`).then(res2json).then(r => { setGameInfo(r.data ?? {}) })
  createEffect(() => {
    if (store.gameAccount.username) {
      fetchGameInfo()
    }
  })

  function toggleRepeatingSwitch(on) {
    setRepeatingSwitch(e => on)
    fetch(`/api/${account().game_code}/settings/repeating`, { method: on ? 'PUT' : 'DELETE' }).then(res2json)
    addToast({ message: on ? '追码已开启' : '追码已关闭' })
  }

  function toggleGameSwitch(on) {
    setGameSwitch(e => on)
    fetch(`/api/${account().game_code}/settings/game`, { method: on ? 'PUT' : 'DELETE' }).then(res2json)
    addToast({ message: on ? '游戏已开启' : '游戏已关闭' })
  }

  function toggleRegisterSwitch(on) {
    setRegisterSwitch(e => on)
    fetch(`/api/${account().game_code}/settings/register`, { method: on ? 'PUT' : 'DELETE'}).then(res2json)
    addToast({ message: on ? '玩家注册已开启' : '玩家注册已关闭' })
  }

  return (
    <>
      <div class="flex flex-col gap-4 lg:gap-6">
        <div>
          <div class="text-orange-500">本游戏 {games.names[account().game] ?? account().game} 仅供娱乐，请勿用于非法用途</div>
          <div>
            <span class="pr-2">欢迎您 {account().username},</span>
            <span class={expiresDays(expiresAt()) < 0 ? 'text-error' : ''}>剩 {expiresDays(expiresAt())} 天</span>
          </div>
        </div>
        <div class="flex flex-wrap gap-3">
          {access('game.account.edit') && (
            <>
              <label class="inline-flex items-center gap-2 cursor-pointer select-none">
                <span>开启追码: {repeatingSwitch() ? '开启' : '关闭'}</span>
                <input type="checkbox" onChange={e => toggleRepeatingSwitch(e.target.checked)} class="toggle toggle-success" checked={repeatingSwitch()} disabled={!store.gameAccount.username} />
              </label>
              <label class="inline-flex items-center gap-2 cursor-pointer select-none">
                <span>开盘状态: {gameSwitch() ? '开盘' : '关闭'}</span>
                <input type="checkbox" onChange={e => toggleGameSwitch(e.target.checked)} class="toggle toggle-success" checked={gameSwitch()} disabled={!store.gameAccount.username} />
              </label>
            </>
          )}
          {access('settings.edit') && (
            <label class="inline-flex items-center gap-2 cursor-pointer select-none">
              <span>玩家注册: {registerSwitch() ? '开启' : '关闭'}</span>
              <input type="checkbox" onChange={e => toggleRegisterSwitch(e.target.checked)} class="toggle toggle-success" checked={registerSwitch()} disabled={!store.gameAccount.username} />
            </label>
          )}
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4 lg:gap-6">
          <div class="flex flex-col gap-4 lg:gap-6">
            <UserFrontendInfo />
            {access('game.account.edit') && (
              <GameAccount gameInfo={gameInfo} validBetAmount={validBetAmount} />
            )}
          </div>
        </div>
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}
      </Portal>
    </>
  )
}

function GameAccount({ gameInfo, validBetAmount }) {
  const [site, setSite] = createSignal('')
  const [code, setCode] = createSignal('')
  const [username, setUsername] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [endpoint, setEndpoint] = createSignal('')
  const [endpointUrl, setEndpointUrl] = createSignal('')

  const [siteOptions, setSiteOptions] = createSignal([])
  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})
  const [sysTime, setSysTime] = createSignal(dayjs().format('YYYY-MM-DD HH:mm:ss'))

  const hasSearchCode = createMemo(() => site().startsWith('UP55') || site() === 'PC28')
  const hasCustomUrl = account().game !== 'M8'

  onMount(() => {
    fetch(`/api/${account().game_code}/account/site-options`).then(res2json).then(r => { setSiteOptions(r.data ?? []) })
  })

  createEffect(() => {
    const url = store.gameAccount.endpoint_url ?? ''
    setEndpointUrl(url)
    setEndpoint(url ? '' : (store.gameAccount.site?.endpoint ?? ''))
  })

  let sysTimer
  onMount(() => {
    sysTimer = setInterval(() => setSysTime(dayjs().format('YYYY-MM-DD HH:mm:ss')), 1000)
  })
  onCleanup(() => { clearInterval(sysTimer) })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setErrors({})
    setSending(true)
    return fetch(`/api/${account().game_code}/account/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(hasSearchCode() ? {
        site: site(),
        code: code(),
        username: username(),
        password: password(),
        endpoint: endpointUrl()
      } : {
        site: site(),
        username: username(),
        password: password(),
        endpoint: endpointUrl()
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        if (r.data.default?.startsWith('Unable to connect')) {
          setErrors({ ...r.data, default: '网址不正确或线路失效' })
        } else {
          setErrors(r.data)
        }
      } else {
        setStore('gameAccount', reconcile(r.data ?? {}))
        updateAccount({ site: site() })
      }
    }).finally(() => { setSending(false) })
  }

  async function logout() {
    setSending(true)
    return fetch(`/api/${account().game_code}/account/logout`, { method: 'POST' }).then(res2json).then(() => {
      setStore('gameAccount', reconcile({}))
      updateAccount({ site: '' })
    }).finally(() => { setSending(false) })
  }

  async function updateEndpoint(e) {
    if (e.target.checked && endpoint() !== e.target.value) {
      setEndpoint(e.target.value)
      setErrors({})
      setSending(true)
      return fetch(`/api/${account().game_code}/account/endpoint`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: endpoint()
        })
      }).then(res2json).then(r => {
        if (r.status >= 400) {
          setErrors(r.data)
        } else {
          addToast({ message: '已切换线路' })
        }
      }).finally(() => { setSending(false) })
    }
  }

  async function onSaveCustom(e) {
    e.preventDefault()
    setErrors({})
    setSending(true)
    return fetch(`/api/${account().game_code}/account/endpoint`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: endpointUrl()
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        addToast({ message: '已切换线路' })
      }
    }).finally(() => { setSending(false) })
  }

  return (
    <>
      {store.gameAccount.username ? (
        <div class="card bg-base-100 border">
          <div class="card-body gap-4">
            <div class="flex items-center justify-between">
              <div class="card-title">网盘</div>
              <button type="button" onClick={() => logout()} class="btn btn-sm" disabled={sending()}>退出</button>
            </div>
            <div class="space-y-4">
              {store.gameAccount.endpoints.map(row => (
                <label class="flex items-center space-x-4 cursor-pointer">
                  <input type="radio" name="endpoint" class="radio" value={row.endpoint} checked={endpoint() === row.endpoint} onChange={e => updateEndpoint(e)} />
                  <span>{row.title}</span>
                </label>
              ))}
              <form onSubmit={onSaveCustom}>
                <label class="flex items-center space-x-4 cursor-pointer">
                  <input type="radio" name="endpoint" class="radio" value="" checked={endpoint() === ''} onChange={e => setEndpoint('')} />
                  {endpoint() === '' ? (
                    <div class="join w-full">
                      <input type="url" name="endpoint_url" value={endpointUrl()} onChange={e => setEndpointUrl(e.target.value)} class="input input-bordered flex-1 join-item" placeholder="自定义线路网址" required />
                      <button type="submit" class="btn join-item">保存</button>
                    </div>
                  ) : (
                    <div>自定义线路网址</div>
                  )}
                </label>
                {errors().endpoint && (<div class="text-error">{errors().endpoint}</div>)}
                {errors().default && (<div class="text-error">{errors().default}</div>)}
              </form>
            </div>
            <div>会员 [{store.gameAccount.username}] 剩余积分 [{store.gameAccount.score.toFixed(2)}]</div>
            <div>{gameInfo().num} 期</div>
            <div>系统时间: {sysTime()}</div>
            <div>封盘时间: {gameInfo().close_time}</div>
            <div>开盘时间: {gameInfo().open_time}</div>
            <div>本期投注积分为 [{(validBetAmount() / 100).toFixed(2)}]</div>
          </div>
        </div>
      ) : (
        <div class="card bg-base-100 border">
          <div class="card-body gap-4">
            <div class="card-title">网盘登陆</div>
            <form onSubmit={submit} class="flex flex-col gap-6">
              <div class="form-control">
                <div class="label"><span class="label-text">网盘</span></div>
                <select onChange={e => setSite(e.target.value)} class="select select-bordered" required>
                  <option value="" disabled selected={site() === ''}>请选择网盘</option>
                  {siteOptions().map(row => (
                    <option value={row.site} selected={site() === row.site}>{row.name}</option>
                  ))}
                </select>
                {errors().site && (<div class="label"><span class="label-text-alt text-error">{errors().site}</span></div>)}
              </div>
              {hasSearchCode() && (
                <div class="form-control">
                  <div class="label"><span class="label-text">搜索码</span></div>
                  <input type="text" value={code()} onChange={e => setCode(e.target.value)} class="input input-bordered" placeholder="请输入网盘 搜索码" required />
                  {errors().code && (<div class="label"><span class="label-text-alt text-error">{errors().code}</span></div>)}
                </div>
              )}
              <div class="form-control">
                <div class="label"><span class="label-text">会员账号</span></div>
                <input type="text" value={username()} onChange={e => setUsername(e.target.value)} class="input input-bordered" placeholder="请输入网盘 会员 账号" required />
                {errors().username && (<div class="label"><span class="label-text-alt text-error">{errors().username}</span></div>)}
              </div>
              <div class="form-control">
                <div class="label"><span class="label-text">会员密码</span></div>
                <input type="password" onChange={e => setPassword(e.target.value)} class="input input-bordered" placeholder="请输入网盘 会员 密码" required />
                {errors().password && (<div class="label"><span class="label-text-alt text-error">{errors().password}</span></div>)}
              </div>
              {hasCustomUrl && (
                <div class="form-control">
                  <div class="label"><span class="label-text">线路网址(可留空)</span></div>
                  <input type="url" value={endpointUrl()} onChange={e => setEndpointUrl(e.target.value)} class="input input-bordered" placeholder="仅在默认线路失效时选填" />
                  {errors().endpoint && (<div class="label"><span class="label-text-alt text-error">{errors().endpoint}</span></div>)}
                </div>
              )}
              {errors().default && (<div class="text-error">{errors().default}</div>)}
              <div class="form-control">
                <button type="submit" class="btn btn-success text-white w-full" disabled={sending()}>提交登陆</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

function UserFrontendInfo() {
  let elQRCode

  const url = `${import.meta.env.VITE_CHAT_URL_PREFIX}/${account().game_code}/welcome`

  onMount(() => {
    elQRCode.innerHTML = ''
    new window.QRCode(elQRCode, { text: url, width: 128, height: 128, correctLevel: window.QRCode.CorrectLevel.H })
  })

  function copyToClipboard() {
    navigator.clipboard.writeText(url)
    addToast({ message: '已复制到剪贴板' })
  }

  return (
    <div class="card bg-base-100 border">
      <div class="card-body gap-4">
        <div class="card-title">玩家前台</div>
        <div class="flex items-center justify-between gap-1">
          <div class="break-all">网址: <span class="select-all">{url}</span></div>
          <div class="tooltip" data-tip="复制">
            <button type="button" onClick={() => copyToClipboard()} class="btn btn-ghost btn-sm"><span class="w-6"><CopyLine /></span></button>
          </div>
        </div>
        <div ref={elQRCode}></div>
      </div>
    </div>
  )
}
